/**
 * Timezone helpers for scheduling.
 *
 * The platform serves Australian customers across multiple states with
 * differing timezones (and DST rules). All scheduling-related date and
 * slot displays should run through these helpers so the user sees the
 * timezone label and the rendered time matches the on-site time.
 */

import { format } from 'date-fns';
import { getSlot } from '../constants/scheduling';

/**
 * IANA timezone IDs for each Australian state/territory.
 *
 * Notes:
 *  - QLD does not observe DST.
 *  - NSW/ACT/VIC/TAS observe AEST/AEDT and Sydney is the canonical IANA id.
 *  - NT does not observe DST and uses ACST year-round.
 *  - SA observes ACST/ACDT.
 *  - WA does not observe DST.
 */
const STATE_TIMEZONES: Record<string, string> = {
    NSW: 'Australia/Sydney',
    ACT: 'Australia/Sydney',
    VIC: 'Australia/Melbourne',
    QLD: 'Australia/Brisbane',
    SA: 'Australia/Adelaide',
    NT: 'Australia/Darwin',
    WA: 'Australia/Perth',
    TAS: 'Australia/Hobart',
};

const FALLBACK_TZ = 'Australia/Sydney';

export interface JobLike {
    site_state?: string | null;
    site_timezone?: string | null;
}

/**
 * Resolve the timezone the job's on-site work happens in.
 *
 * Order of precedence:
 *  1. An explicit `site_timezone` field on the job (if ever populated).
 *  2. Lookup by `site_state` (NSW → Australia/Sydney, etc.).
 *  3. Browser timezone.
 *  4. Australia/Sydney as a final fallback.
 */
export function getJobTimezone(job: JobLike | null | undefined): string {
    if (!job) return browserTimezone();
    if (job.site_timezone) return job.site_timezone;
    if (job.site_state) {
        const tz = STATE_TIMEZONES[job.site_state.toUpperCase()];
        if (tz) return tz;
    }
    return browserTimezone();
}

function browserTimezone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TZ;
    } catch {
        return FALLBACK_TZ;
    }
}

/**
 * Short timezone abbreviation suitable for inline display (e.g. "AEST").
 */
export function getTimezoneAbbreviation(
    date: Date | string,
    timezone: string,
): string {
    const d = typeof date === 'string' ? new Date(date) : date;
    try {
        const parts = new Intl.DateTimeFormat('en-AU', {
            timeZone: timezone,
            timeZoneName: 'short',
        }).formatToParts(d);
        return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
    } catch {
        return '';
    }
}

/**
 * Format a date in the job's timezone using a date-fns format string.
 *
 * Note: `date-fns` formats in the host timezone. For accuracy across DST
 * boundaries we render via `Intl.DateTimeFormat` for the date parts and only
 * fall back to date-fns for purely date-only formatting (where TZ doesn't
 * change the answer in practice for AU dates we're displaying in the
 * morning/day grain).
 */
export function formatJobDate(
    date: Date | string,
    // The timezone arg is intentionally part of the API even though the
    // current implementation defers to date-fns (host TZ): all scheduling
    // surfaces should plumb the resolved tz through so we can swap to a
    // tz-aware formatter (date-fns-tz) without a downstream refactor.
    _timezone: string,
    fmt: string = 'EEEE, d MMMM yyyy',
): string {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (Number.isNaN(d.getTime())) return '';
    // For date-only display (the common case in scheduling) we use date-fns
    // directly because the input is normally a YYYY-MM-DD string already
    // anchored to the local day. Time-of-day formats include the tz suffix.
    const formatted = format(d, fmt);
    return formatted;
}

/**
 * Render a time-slot for the customer-facing UI in the job's timezone.
 *
 * Examples:
 *   "Morning (8 am – 12 pm AEST)"
 *   "Full Day (8 am – 5 pm AEDT)"
 *   "" — when value is null/empty/unknown
 */
export function formatJobSlot(
    value: string | null | undefined,
    job: JobLike | null | undefined,
    referenceDate: Date | string = new Date(),
): string {
    const slot = getSlot(value);
    if (!slot) return '';
    const tz = getJobTimezone(job);
    const abbr = getTimezoneAbbreviation(referenceDate, tz);
    const tzSuffix = abbr ? ` ${abbr}` : '';
    // Rebuild the label with an injected tz so the customer sees on-site time.
    const start = formatHour(slot.startHour);
    const end = formatHour(slot.endHour);
    return `${slot.shortLabel} (${start} – ${end}${tzSuffix})`;
}

function formatHour(hour: number): string {
    if (hour === 0) return '12 am';
    if (hour === 12) return '12 pm';
    if (hour < 12) return `${hour} am`;
    return `${hour - 12} pm`;
}

/**
 * Checks whether a given Date falls on a weekend (Saturday or Sunday) in Australia/Sydney.
 *
 * Uses `Intl.DateTimeFormat` with `timeZone: 'Australia/Sydney'`, supported natively
 * in modern React Native / Hermes (RN 0.70+, Expo 55+) and Node.js. If `Intl` or timezone lookup
 * is unsupported in an environment, falls back to fixed AEST offset (UTC+10) with
 * the documented limitation that DST (AEDT UTC+11) is not accounted for in that fallback.
 */
let sydneyWeekdayFormatter: Intl.DateTimeFormat | null = null;
function getSydneyWeekdayFormatter(): Intl.DateTimeFormat {
    if (!sydneyWeekdayFormatter) {
        sydneyWeekdayFormatter = new Intl.DateTimeFormat('en-US', {
            timeZone: FALLBACK_TZ,
            weekday: 'short',
        });
    }
    return sydneyWeekdayFormatter;
}

export function isWeekendInSydney(date: Date): boolean {
    try {
        const weekday = getSydneyWeekdayFormatter().format(date);
        return weekday === 'Sat' || weekday === 'Sun';
    } catch {
        // Fallback: Fixed-offset AEST (UTC+10).
        // Limitation: Does not adjust for daylight saving time (AEDT, UTC+11) between October and April.
        const aestTime = new Date(date.getTime() + 10 * 60 * 60 * 1000);
        const day = aestTime.getUTCDay();
        return day === 0 || day === 6;
    }
}

/**
 * Adds business days (Monday-Friday in Australia/Sydney) to a timestamptz Date,
 * preserving exact time of day while skipping weekends evaluated in Sydney time.
 *
 * Throws RangeError if days > 365, mirroring the SQL function's ERRCODE 22003.
 *
 * Parity with PostgreSQL public.add_business_days(timestamptz, integer): the two agree
 * exactly except when the window spans an Australian DST transition, where they differ by
 * one hour. This function steps in fixed 24h increments and so preserves the UTC instant's
 * time-of-day, whereas the SQL function steps in Sydney wall-clock space and so preserves
 * the Sydney time-of-day. Example: 2026-10-01T00:00Z + 3 business days returns
 * 2026-10-06T00:00Z here and 2026-10-05T23:00Z in SQL.
 *
 * This is acceptable because the server-computed jobs.offer_expires_at is authoritative and
 * is simply displayed when present; this helper is only the fallback for legacy rows that
 * predate that column. Do not use it to compute a deadline that is then persisted.
 */
export function addBusinessDaysSydney(from: Date, days: number = 5): Date {
    if (!from || isNaN(from.getTime())) {
        return from;
    }
    const result = new Date(from.getTime());
    if (!Number.isFinite(days) || days <= 0) {
        return result;
    }
    if (days > 365) {
        throw new RangeError('days exceeds maximum allowed business days (365)');
    }

    let added = 0;
    const targetDays = Math.floor(days);

    while (added < targetDays) {
        result.setTime(result.getTime() + 24 * 60 * 60 * 1000);
        if (!isWeekendInSydney(result)) {
            added++;
        }
    }

    return result;
}
